import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Campaign as CampaignIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState<any>(null);

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const response = await adminAPI.getCurrentAdmin();
        console.log('Admin response:', response); // 디버깅용
        setAdmin(response.data.data); // response.data.data에서 실제 admin 정보 가져오기
      } catch (error) {
        console.error('Failed to fetch admin:', error);
      }
    };
    fetchAdmin();
  }, []);

  const statsCards = [
    {
      title: 'Total Banners',
      value: '24',
      change: '+12%',
      changeType: 'positive',
      icon: <CampaignIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: 'primary.main',
    },
    {
      title: 'Active Campaigns',
      value: '18',
      change: '+8%',
      changeType: 'positive',
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: 'success.main',
    },
    {
      title: 'Total Admins',
      value: '5',
      change: '+2',
      changeType: 'positive',
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      color: 'info.main',
    },
    {
      title: 'Notifications',
      value: '3',
      change: 'New',
      changeType: 'warning',
      icon: <NotificationsIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: 'warning.main',
    },
  ];

  const quickActions = [
    {
      title: 'Create Main Banner',
      description: 'Add a new main banner advertisement',
      icon: <CampaignIcon />,
      path: '/banners/main/create',
      color: 'primary',
    },
    {
      title: 'Manage Text Ads',
      description: 'Create and edit text-based advertisements',
      icon: <CampaignIcon />,
      path: '/banners',
      color: 'secondary',
    },
    {
      title: 'Admin Settings',
      description: 'Manage admin accounts and permissions',
      icon: <PeopleIcon />,
      path: '/admin',
      color: 'info',
    },
  ];

  const recentActivities = [
    { text: 'New main banner created', time: '2 hours ago', type: 'success' },
    { text: 'Text ad campaign updated', time: '4 hours ago', type: 'info' },
    { text: 'Admin login from new IP', time: '6 hours ago', type: 'warning' },
    { text: 'Banner performance report generated', time: '1 day ago', type: 'success' },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <CheckCircleIcon color="info" />;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Welcome Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Welcome back, {admin?.name || 'Admin'}! 👋
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          Here's what's happening with your Kaiascan Admin Dashboard today.
        </Typography>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      {card.title}
                    </Typography>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {card.value}
                    </Typography>
                    <Chip
                      label={card.change}
                      size="small"
                      color={card.changeType === 'positive' ? 'success' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                  <Avatar sx={{ backgroundColor: 'grey.100', width: 56, height: 56 }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions and Recent Activities */}
      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Quick Actions
              </Typography>
              <List>
                {quickActions.map((action, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      button
                      onClick={() => navigate(action.path)}
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ backgroundColor: `${action.color}.light`, color: `${action.color}.main` }}>
                          {action.icon}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={action.title}
                        secondary={action.description}
                        primaryTypographyProps={{ fontWeight: 'medium' }}
                      />
                      <ArrowForwardIcon color="action" />
                    </ListItem>
                    {index < quickActions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activities */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Recent Activities
              </Typography>
              <List>
                {recentActivities.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {getActivityIcon(activity.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                    {index < recentActivities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Status */}
      <Card elevation={2} sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            System Status
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Chip label="All Systems Operational" color="success" variant="outlined" />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" color="textSecondary">Database</Typography>
                <Chip label="Online" color="success" size="small" />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" color="textSecondary">API</Typography>
                <Chip label="Online" color="success" size="small" />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="body2" color="textSecondary">Storage</Typography>
                <Chip label="Online" color="success" size="small" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Dashboard;
